import { Quill } from 'react-quill-new';

const Delta = Quill.import('delta');
const NON_BREAKING_SPACE = '\u00a0';

// Quill 2 turns non-breaking spaces into regular ones while loading HTML.
// Its own text handling is repeated here up to that last step.
export const keepNonBreakingSpaces = (
  node: Node,
  delta: InstanceType<typeof Delta>
): InstanceType<typeof Delta> => {
  const sourceText = node.textContent ?? '';
  const loadedText = delta.ops[0]?.insert;

  if (delta.ops.length !== 1 || !sourceText.includes(NON_BREAKING_SPACE)) {
    return delta;
  }

  const collapsedText = sourceText
    .replace(/[^\S\u00a0]/g, ' ')
    .replace(/ {2,}/g, ' ');
  const restoredText = [
    collapsedText,
    collapsedText.slice(1),
    collapsedText.slice(0, -1),
    collapsedText.slice(1, -1),
  ].find((text) => text.split(NON_BREAKING_SPACE).join(' ') === loadedText);

  return restoredText ? new Delta().insert(restoredText) : delta;
};

const createStoredList = (listType: string): HTMLElement => {
  const storedList = document.createElement(
    listType === 'ordered' ? 'ol' : 'ul'
  );

  if (listType === 'checked' || listType === 'unchecked') {
    storedList.setAttribute('data-checked', String(listType === 'checked'));
  }

  return storedList;
};

const replaceEditorList = (editorList: Element) => {
  let storedList: HTMLElement | undefined;
  let storedListType: string | undefined;

  for (const item of Array.from(editorList.children)) {
    const listType = item.getAttribute('data-list') ?? 'bullet';

    if (!storedList || listType !== storedListType) {
      storedList = createStoredList(listType);
      storedListType = listType;
      editorList.before(storedList);
    }

    item.removeAttribute('data-list');
    storedList.append(item);
  }

  editorList.remove();
};

const replaceEditorCodeBlock = (editorCodeBlock: Element) => {
  const storedCodeBlock = document.createElement('pre');

  storedCodeBlock.className = 'ql-syntax';
  storedCodeBlock.setAttribute('spellcheck', 'false');
  storedCodeBlock.textContent = Array.from(editorCodeBlock.children)
    .map((line) => `${line.textContent}\n`)
    .join('');
  editorCodeBlock.replaceWith(storedCodeBlock);
};

export function toStoredEditorHtml(editorHtml: string): string {
  const template = document.createElement('template');

  template.innerHTML = editorHtml;
  template.content.querySelectorAll('.ql-ui').forEach((node) => node.remove());
  template.content.querySelectorAll('ol').forEach(replaceEditorList);
  template.content
    .querySelectorAll('.ql-code-block-container')
    .forEach(replaceEditorCodeBlock);

  return template.innerHTML;
}
